import { Reveal, LineReveal, ImageReveal } from "../components/motion/Reveal";
import { IMG } from "../lib/site";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="bg-bg">
      {/* Hero-lite */}
      <section className="relative pt-40 pb-24 md:pt-56 md:pb-32 overflow-hidden" data-testid="about-hero">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="overline text-terracotta mb-6">About the Haveli</div>
            <LineReveal
              className="font-display leading-[0.92] tracking-[-0.02em]"
              lineClassName="block text-6xl md:text-7xl lg:text-8xl text-ink font-bold"
              lines={["A haveli", <span key="i" className="italic text-terracotta">that remembers.</span>]}
            />
            <Reveal delay={0.4} className="mt-8 max-w-lg text-ink-soft leading-relaxed">
              <p>
                This late 17th Century Haveli built by the Bohara Braj Bhushanjee family
                is situated just below the Palace on the main road. The family gave four
                Prime Ministers to the Bundi State — all in the 19th century — and it is
                still the residence of the same family.
              </p>
              <p className="mt-4">
                Since 1990 we have converted this Haveli into a Heritage Hotel and Museum.
                We welcome travellers into the parts of the house we can share — eighteen rooms,
                painted courtyards, a small in-house museum, and a rooftop that looks straight
                to the palace lit up at night.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal>
              <div className="arch-mask border border-[#E5E1D8]">
                <ImageReveal src={IMG.aboutPortrait} alt="Painted courtyard" className="aspect-[3/4]"/>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Museum */}
      <section className="py-24 md:py-32 border-t border-[#E5E1D8]" data-testid="about-museum">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <Reveal>
              <div className="overline text-terracotta">Interior &amp; Museum</div>
              <h2 className="font-display font-bold text-5xl md:text-6xl mt-5 leading-[0.95] text-ink">
                Bundi School<br/><span className="italic text-terracotta">Frasco.</span>
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed max-w-md">
                The main attraction of the haveli is its collection of Bundi School miniatures —
                Raginies, court scenes, hunting scenes etc. Carved sandstone pillars and arches
                complete the Rajput vocabulary.
              </p>
              <p className="mt-4 text-ink-soft leading-relaxed max-w-md">
                Old photographs of the Bundi rulers and of our family line the tibaries. Everything you
                see has been in this house for over a century.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7 space-y-4 md:space-y-6">
            <Reveal>
              <div className="border border-[#E5E1D8] overflow-hidden">
                <ImageReveal src={IMG.aboutMural} alt="Wall murals — Bundi School" className="aspect-[16/11]"/>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <Reveal delay={0.1}>
                <div className="border border-[#E5E1D8] overflow-hidden">
                  <ImageReveal src={IMG.aboutCourtyard} alt="Courtyard" className="aspect-[4/5]"/>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="border border-[#E5E1D8] overflow-hidden mt-8 md:mt-14">
                  <ImageReveal src={IMG.interior2} alt="Antique collection" className="aspect-[4/5]"/>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Photo journey */}
      <section className="bg-bg py-24 md:py-32" data-testid="about-journey">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="overline text-terracotta">A walk through the haveli</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-[0.95] text-ink">
                Every threshold,<br/><span className="italic text-terracotta">a story.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-6 md:grid-cols-12 gap-3 md:gap-4">
            <Reveal className="col-span-6 md:col-span-8 aspect-[16/10]">
              <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                <ImageReveal src="https://customer-assets-v7afamib.emergentagent.net/job_kipling-redesign-1/artifacts/cxgybkxl_V-7.jpg" alt="Bundi Palace at dusk from the haveli terrace" className="w-full h-full"/>
              </div>
            </Reveal>
            <Reveal delay={0.05} className="col-span-6 md:col-span-4 aspect-[4/5]">
              <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                <ImageReveal src="https://customer-assets-v7afamib.emergentagent.net/job_kipling-redesign-1/artifacts/qvi90hk5_P-7.jpg" alt="Painted alcove entrance" className="w-full h-full"/>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="col-span-6 md:col-span-6 aspect-[16/10]">
              <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                <ImageReveal src="https://customer-assets-v7afamib.emergentagent.net/job_kipling-redesign-1/artifacts/3x9lwrkp_P-16.jpg" alt="Chandelier-lit painted courtyard" className="w-full h-full"/>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="col-span-6 md:col-span-6 aspect-[16/10]">
              <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                <ImageReveal src="https://customer-assets-v7afamib.emergentagent.net/job_kipling-redesign-1/artifacts/e0l3kqek_P-1.JPG" alt="Museum with glass-floor courtyard" className="w-full h-full"/>
              </div>
            </Reveal>
            <Reveal delay={0.2} className="col-span-6 md:col-span-12 aspect-[21/9]">
              <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                <ImageReveal src="https://customer-assets-v7afamib.emergentagent.net/job_kipling-redesign-1/artifacts/ryv021ia_R-B-7-2.jpg" alt="Guest room with painted ceiling" className="w-full h-full"/>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <p className="max-w-md text-ink leading-relaxed">
                Painted courtyards, in-house museum, palace views at dusk, and rooms sleeping under
                original Bundi School murals — welcome home.
              </p>
              <Link to="/heritage-hotel#booking" className="btn-pill btn-primary" data-testid="journey-cta">
                Reserve your stay
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
